import { createSystemSidebarCards } from '../sidebar/system_sidebar_cards.mjs';
import { readOwnerSheets } from '../project/system_sheet_reader.mjs';
import { createSystemOrderCommandService } from '../project/system_order_command_service.mjs';
import { registerSystemHistoryScope, recordSystemUndo, completeSystemUndo, cancelSystemUndo, restoreSystemHistory, systemHistoryAvailable } from '../services/system_history_service.mjs';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { createLegacyAssetAppearance, readSystemAssetBindings } from '../../plugin-contracts/asset_binding_contract.mjs';
import { assetPinsAuthorize, readSystemAssetPins } from '../../plugin-contracts/asset_pin_contract.mjs';
import { applyLegacyAppearance, deriveLegacySvgRegionColors, legacyAppearanceKey } from './legacy_asset_appearance.mjs';
import { assetReferenceKey, createAssetReference } from '../../plugin-contracts/asset_reference_contract.mjs';
import { assetVariantId, createAssetVariantParameters } from '../../plugin-contracts/asset_variant_identity.mjs';
import { createAssetPackageReader } from './asset_package_reader.mjs';
import { packagePlanProjectionPath } from '../render/plan_projection_paths.mjs';
import { createArticleReadPort } from '../articles/article_read_port.mjs';
import { S } from './app_state.js?v=20260817c';
import { AppRuntime } from './app_runtime.js?v=20260706d';
import { createSystemCommandService } from '../project/system_command_service.js';
import { claimPointerCommit } from '../interaction/pointer_commit_ownership.mjs';
import { createSystemContextPorts, readSystemInteractionContext } from './system_runtime_context.js';
import { createOwnerSelectionFields, readSystemSelectionContext, systemStateNamespace } from './system_runtime_selection.js';
import { normalizeProjectLevels } from '../project/project_level_constants.js';
import { getUserPreference, setUserPreference, userPreferenceOwnerId } from '../user/user_preferences.js?v=20260812a';
import { showDrawingFeedback } from '../ui/drawing_feedback.js?v=20260717a';
import { activeTool, isPlainPrimaryPointer, constrainPointToOrthogonal } from '../interaction/tool_service.js?v=20260904f';
import { resolvePlanLabelMetrics } from '../render/plan_label_options.js?v=20260828b';
import { selectedPrintPlanSystemObjectIds } from '../print/print_plan_group_filter.js?v=20260904c';
import { cloneSystemData, normalizeSystemDataContract } from '../../plugin-contracts/system_data_contract.mjs';
import { getProjectModelRevision, markProjectModelChanged } from '../project/project_revision_service.js';
import { projectStateMode } from '../project/project_state_service.js?v=20260904f';
import { recordUndo } from '../services/history_service.js?v=20260903c';
import { makeId } from '../utils/id_service.js?v=20260706d';
import { parseColorHex } from '../utils/color_utils.js?v=20260706d';
import { hasPermission } from '../user/current_user.js?v=20260722a';
import { getActiveLanguage, t } from '../i18n/i18n_service.js?v=20260722a';
import { getCanvasSize, getPlanSvg, screenToWorld, setStatus, svgEl, worldSvgRect, worldToScreen } from '../render/plan_renderer.js?v=20260904f';
import { isPrintPlanCaptureInProgress, shouldRenderPrintPlanCoreGroup } from '../print/print_plan_context.js?v=20260826b';
import * as SelectionService from '../selection/selection_service.js?v=20260904f';
import { hasMultiSelection } from '../selection/selection_controller.js?v=20260904f';
import * as DrawingSelection from '../selection/drawing_selection_manager.js?v=20260706d';
import * as DrawingRegistry from '../objects/drawing_object_registry.js?v=20260904f';
import { invalidateDrawingObjectSnapshot } from '../objects/drawing_object_snapshot_service.js?v=20260904f';
import * as DrawingTransforms from '../interaction/drawing_transform_controller.js?v=20260904f';
import * as PlacementTools from '../interaction/placement_tool_service.js?v=20260904f';
import { drawImportedSvgAsset, loadImportedSvgAsset, parseImportedSvgAsset } from '../objects/imported_svg_asset.js?v=20260831c';
import { computeDimensionSnapMetadata } from '../services/dimension_geometry_client.js?v=20260812a';
import { ArticleDatabaseCatalog } from '../articles/article_database_catalog.js?v=20260831f';
import { renderGroupList } from '../sidebar/sidebar_group_list.js?v=20260903c';
import { colorCatalogEntries, colorValueKey, findColor, firstColor, systemColorCatalogIds, normalizeColorValue, resolveColorHex, resolveColorLabel, serializeColorValue } from '../../../colors/color_catalog_registry.js?v=20260720a';
import { bindColorPicker, colorPickerSwatchStyle, renderColorPicker } from '../../../colors/color_picker.js?v=20260720a';

let threeModules = null;
let threePromise = null;
function prepare3D() {
  if (!threePromise) {
    threePromise = import('./system_runtime_3d.js').then(module => {
      threeModules = module;
      return module;
    }).catch(error => { threePromise = null; throw error; });
  }
  return threePromise;
}
function threeCall(name, args) {
  if (!threeModules) throw new Error('Die 3D-Laufzeit muss vor dem Szenenaufbau vorbereitet werden.');
  return threeModules[name](...args);
}
function readonly(value) {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(readonly);
    Object.freeze(value);
  }
  return value;
}
function optionalStateKey(value) {
  if (value === undefined) return null;
  if (typeof value !== 'string' || !/^[A-Za-z][A-Za-z0-9_]*$/.test(value)
    || ['items', 'obstacleIds', 'multiSelection', 'constructor', 'prototype', '__proto__'].includes(value)) {
    throw new TypeError('Ungueltiger systemeigener Zustandsschluessel.');
  }
  return value;
}

function selectionKind(value = 'item') {
  if (typeof value !== 'string' || !/^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(value)) {
    throw new TypeError('Ungueltige systemeigene Auswahlart.');
  }
  return value;
}

function staticAssetPaths(paths = []) {
  if (!Array.isArray(paths) || [...paths].some(path => (
    typeof path !== 'string' || !/^\/assets\/(?:[A-Za-z0-9_.-]+\/)*[A-Za-z0-9_.-]+$/.test(path)
    || path.split('/').some(segment => segment === '.' || segment === '..')
  ))) {
    throw new TypeError('Statische Systemassets benoetigen explizite kanonische /assets/-Pfade.');
  }
  return new Set(paths);
}

/** Narrow, owner-bound ports. Runtime services never become persisted data. */
export function createSystemRuntime(manifest, config = {}, rawDataContract = null) {
  const owner = manifest.systemId;
  const capabilities = new Set(manifest.capabilities || []);
  const contract = manifest.data ? normalizeSystemDataContract(manifest, rawDataContract) : null;
  const disposers = new Set();
  const knownAssetIds = new Set();
  const knownAssetRevisions = new Map();
  const knownAssetRows = new Map();
  let assetReadContext = null;
  const getAssetContext = () => {
    if (!assetReadContext || assetReadContext.project !== S.project || assetReadContext.sheetId !== S.drawingSheets?.activeSheetId) {
      assetReadContext = { project: S.project, sheetId: S.drawingSheets?.activeSheetId };
    }
    return assetReadContext;
  };
  let boundModelReader = null, boundModelPromise = null;
  disposers.add(() => boundModelReader?.dispose());
  const packageReader = capabilities.has('assets') ? createAssetPackageReader({
    getContext: getAssetContext,
    authorize(id, reference, locator) { authorizeAsset(id, reference, locator); }
  }) : null;
  function committedBindings() {
    if (!contract?.assetBindingsPath) return null;
    let data = S.project;
    for (const segment of contract.path) data = data?.[segment];
    return readSystemAssetBindings(contract, data);
  }
  // Committed order-wide pins of this owner (all sheets); never a command draft.
  function committedOrderPins() {
    const order = contract?.orderData;
    if (!order?.assetPinsPath) return null;
    let data = S.project;
    for (const segment of order.path) data = data?.[segment];
    return readSystemAssetPins(order, data);
  }
  // A requested entry authorizes its base pin and exactly its own variant identity.
  const requestedVariantOf = (entry, locator) => entry.state === 'requested' && entry.base.assetObjectId === locator.assetObjectId
    && entry.base.revisionId === locator.revisionId && entry.variantId === locator.variantId;
  function authorizeAsset(id, reference, locator = null) {
    if (knownAssetIds.has(String(id))) return;
    // A pinned base revision of the order also covers its derived variants.
    if (assetPinsAuthorize(committedOrderPins(), reference, locator)) return;
    // A historical read may use exactly a pin in this owner's committed data.
    // Never use a mutable command draft, another sheet or a caller-supplied list.
    const bindings = committedBindings();
    if (bindings && reference) {
      const key = assetReferenceKey(reference);
      if (bindings.entries.some(entry => (entry.state === 'pinned' && assetReferenceKey(entry.reference) === key)
        || (entry.state === 'requested' && (assetReferenceKey(entry.base) === key || requestedVariantOf(entry, reference))))) return;
    } else if (bindings && locator && bindings.entries.some(entry => requestedVariantOf(entry, locator))) return;
    throw new TypeError('Das Asset gehoert weder zu den geladenen Systemartikeln noch zu einer gespeicherten eigenen Bindung.');
  }
  const canonicalParameters = value => JSON.stringify(Object.keys(value || {}).sort().map(key => [key, value[key]]));
  function checkRequest(value) {
    const base = createAssetReference(value?.base);
    const parameters = createAssetVariantParameters(value?.parameters);
    const variantId = assetVariantId(base.packageDigest, parameters);
    if (base.variantId !== 'default' || (value.variantId !== undefined && value.variantId !== variantId)) throw new TypeError('Asset variant: base/identity mismatch');
    return { base, parameters, variantId };
  }
  async function resolveVariant(value) {
    const { base, parameters, variantId } = checkRequest(value);
    const result = await packageReader.resolve({ assetObjectId: base.assetObjectId, revisionId: base.revisionId, variantId });
    if (result.status !== 'ready') return result;
    // The published variant must belong to exactly this base definition and parameters.
    if (result.reference.packageDigest !== base.packageDigest || result.reference.sourceSha256 !== base.sourceSha256
      || canonicalParameters(result.variant.operation?.parameters) !== canonicalParameters(parameters)) return Object.freeze({ status: 'invalid-package' });
    return Object.freeze({ status: 'ready', reference: result.reference, base });
  }
  if (packageReader) disposers.add(() => { packageReader.dispose(); assetReadContext = null; });
  let presentationReader = null, presentationReaderPromise = null, assetReadGeneration = 0;
  function invalidatePresentation() { assetReadGeneration++; presentationReader?.invalidate(); }
  disposers.add(() => { assetReadGeneration++; presentationReader?.dispose(); });
  const primaryField = optionalStateKey(config.selection?.primary);
  const multipleField = optionalStateKey(config.selection?.multiple);
  const ownSelectionKind = selectionKind(config.selection?.kind);
  const toolKey = optionalStateKey(config.tools?.uiKey);
  const ownStaticAssets = staticAssetPaths(config.assets?.staticPaths);
  const ownerPrefix = owner.toLowerCase().replace(/[^a-z0-9]/g, '');
  const selectionPrefix = systemStateNamespace(manifest, config.selection?.namespace);
  for (const [key, prefix] of [[primaryField, selectionPrefix], [multipleField, selectionPrefix], [toolKey, ownerPrefix]]) {
    if (key && !key.toLowerCase().replace(/[^a-z0-9]/g, '').startsWith(prefix)) {
      throw new TypeError('Legacy-Zustandsschluessel muessen den eigenen Systemnamensraum verwenden.');
    }
  }
  let disposed = false;
  // Monochrome GPU plan document (U08c Block 5) of one explicit revision. Old
  // revisions answer `missing-artifact` with their reference, so a caller can pick
  // a transitional source by the exact source hash. Bytes are hash-checked here and
  // again by the GPU scene through the returned confined path.
  async function loadPlanProjection(reference, viewId) {
    if (disposed) return { status: 'disposed' };
    const context = getAssetContext();
    const result = await packageReader.readArtifact(reference, 'legacyPlan', viewId);
    if (disposed || context !== getAssetContext()) return { status: disposed ? 'disposed' : 'cancelled' };
    if (result.status !== 'ready') return Object.freeze({ status: result.status, reference: result.reference || reference });
    let data;
    try { data = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(result.bytes)); } catch { return { status: 'invalid-package' }; }
    const ref = result.reference, b = data?.bounds;
    const coordinates = [b?.min?.x, b?.min?.y, b?.max?.x, b?.max?.y];
    if (data?.contract !== 'ks.asset-projection' || data.contractVersion !== 1 || data.view?.viewId !== viewId || data.hiddenEdgeMode !== 'none'
      || ['assetId', 'revisionId', 'variantId'].some(key => data.identity?.[key] !== ref[key]) || !Array.isArray(data.regions) || !data.regions.length
      || !coordinates.every(Number.isFinite) || !(coordinates[2] > coordinates[0] && coordinates[3] > coordinates[1])) return { status: 'invalid-package' };
    const [x0, z0, x1, z1] = coordinates, reflected = viewId !== 'front';
    // legacy-v2 placement of a plan view (same as the approved pilot dataset).
    return Object.freeze({ status: 'ready', reference: ref, viewId,
      identity: Object.freeze({ assetId: ref.assetId, revisionId: ref.revisionId, variantId: ref.variantId }),
      projection: Object.freeze({ path: packagePlanProjectionPath(ref, viewId), sha256: result.artifact.sha256,
        byteLength: result.artifact.byteLength, mediaType: 'application/json' }),
      neutralToLegacy2D: Object.freeze([reflected ? -1 : 1, 0, 0, 1, 0, 0]),
      bounds: Object.freeze({ minX: x0, minZ: z0, maxX: x1, maxZ: z1 }),
      placementMetadata: Object.freeze({ widthMm: x1 - x0 + 10, depthMm: z1 - z0 + 10,
        contentBounds: Object.freeze({ minX: reflected ? -x1 : x0, minZ: z0, width: x1 - x0, depth: z1 - z0 }) }) });
  }
  function boundReader() {
    if (!boundModelPromise) boundModelPromise = prepare3D()
      .then(() => threeModules.createBoundModelReader({ packages: packageReader, getContext: getAssetContext }))
      .then(reader => { boundModelReader = reader; if (disposed) reader.dispose(); return reader; })
      .catch(error => { boundModelPromise = null; throw error; });
    return boundModelPromise;
  }
  const preferenceKeys = new Set(config.preferences?.keys || []);
  for (const key of preferenceKeys) {
    if (typeof key !== 'string' || ![ownerPrefix, selectionPrefix].some(prefix => key.startsWith(`${prefix}.`))) {
      throw new TypeError('Fremder Einstellungsnamensraum.');
    }
  }
  const translationAliases = Object.freeze({ ...(config.i18n?.aliases || {}) });
  for (const [key, alias] of Object.entries(translationAliases)) {
    if (!key.startsWith(`${owner}.`) || typeof alias !== 'string'
      || !alias.split('.').some(part => part === selectionPrefix || part === owner)) {
      throw new TypeError('Fremder Uebersetzungsalias.');
    }
  }
  const selectionFields = capabilities.has('selection')
    ? createOwnerSelectionFields(manifest, config.selection || {}, () => disposed) : null;
  if (selectionFields) disposers.add(selectionFields.dispose);
  const contextPorts = createSystemContextPorts(() => disposed);
  let transientProject = S.project;
  let transient = new Map();
  let dragContext = null;
  let dragView = null;
  let hostDragTransient = null;
  const permitted = () => !disposed && (manifest.permissions || []).every(hasPermission);
  let orderCommandService = null;
  const separateHistory = capabilities.has('history');
  const commandService = contract ? createSystemCommandService(contract, {
    getProject: () => S.project,
    getSheetId: () => S.drawingSheets?.activeSheetId,
    getRevision: getProjectModelRevision,
    authorize: permitted,
    isExternallyBusy: () => orderCommandService?.isBusy(),
    completeUndo: separateHistory ? completeSystemUndo : null,
    recordUndo(label) {
      if (separateHistory) return recordSystemUndo(contract, label);
      const previous = { redo: S.history.redo, dirty: S.file?.dirty };
      recordUndo(label);
      return { ...previous, history: S.history, entry: S.history.undo.at(-1) };
    },
    cancelUndo(token) {
      if (separateHistory) { cancelSystemUndo(token); return; }
      if (token && S.history === token.history && S.history.undo.at(-1) === token.entry) {
        S.history.undo.pop();
        S.history.redo = token.redo;
        if (S.file) S.file.dirty = token.dirty;
      }
    },
    commit(meta) {
      const reason = `${owner}:${meta.reason || 'command'}`;
      markProjectModelChanged(reason);
      AppRuntime.requestAppRefresh(false, reason);
    }
  }) : null;

  if (separateHistory) disposers.add(registerSystemHistoryScope(contract, () => SystemRegistry.state.activeSystemId === owner,
    () => permitted() && commandService.isIdle() && !orderCommandService?.isBusy()));

  function historyPort(kind) {
    const restore = direction => {
      if (!permitted()) return false;
      commandService.assertIdle();
      if (orderCommandService?.isBusy()) return false;
      const changed = restoreSystemHistory(owner, direction, kind);
      if (changed) AppRuntime.requestAppRefresh(false, `${owner}:${direction}`);
      return changed;
    };
    return Object.freeze({ undo: () => restore('undo'), redo: () => restore('redo'),
      canUndo: () => permitted() && systemHistoryAvailable(owner, 'undo', kind),
      canRedo: () => permitted() && systemHistoryAvailable(owner, 'redo', kind) });
  }
  if (capabilities.has('orderData')) orderCommandService = createSystemOrderCommandService(contract, {
    getProject: () => S.project, getSheetId: () => S.drawingSheets?.activeSheetId,
    authorize: permitted, assertIdle: commandService.assertIdle,
    recordUndo: (label, kind) => recordSystemUndo(contract, label, kind), completeUndo: completeSystemUndo,
    commit(meta) {
      const reason = `${owner}:${meta.reason || 'order-data'}`;
      markProjectModelChanged(reason);
      AppRuntime.requestAppRefresh(false, reason);
    }
  });

  function ownObject(object) {
    return object && String(object.systemType || object.systemId || '') === owner;
  }
  function objectsForOwner(objects = []) {
    return (Array.isArray(objects) ? objects : []).filter(ownObject);
  }
  function snapshotForOwner(snapshot = []) {
    return (Array.isArray(snapshot) ? snapshot : []).filter(entry => (
      ownObject(entry) && (!entry.state?.systemType || entry.state.systemType === owner)
    ));
  }
  function drawingObject(id) {
    return DrawingRegistry.getDrawingObject(id, { systemType: owner });
  }
  function withHostDraft(update) {
    return commandService ? commandService.applyFromHost(update) : update();
  }
  function replaceSelection(ids = [], options = {}) {
    if (disposed) return [];
    if (options.clearOthers) SelectionService.clearSystemSelectionsExcept(owner);
    const clean = [...new Set(ids.map(id => String(id || '').trim()).filter(Boolean))];
    if (!S.selection) S.selection = {};
    if (primaryField) S.selection[primaryField] = clean.at(-1) || null;
    if (multipleField) S.selection[multipleField] = clean;
    S.selection.items = [
      ...(S.selection.items || []).filter(item => item.owner !== owner),
      // Match SelectionService's canonical shape immediately. A later host
      // sync must not invalidate a selection context merely by adding meta.
      ...clean.map(id => ({ owner, kind: ownSelectionKind, id, meta: {} }))
    ];
    return clean;
  }
  function ownTransient() {
    if (transientProject !== S.project) {
      transientProject = S.project;
      transient = new Map();
    }
    return transient;
  }
  function ownDrag() {
    if (!dragContext || dragContext.project !== S.project
      || dragContext.sheetId !== S.drawingSheets?.activeSheetId
      || S.drag !== dragContext.drag || S.drag.systemId !== owner) return null;
    return dragView;
  }
  function currentDragTransient() {
    if (disposed || !S.drag?.active) {
      hostDragTransient = null;
      return null;
    }
    if (!hostDragTransient || hostDragTransient.project !== S.project
      || hostDragTransient.sheetId !== S.drawingSheets?.activeSheetId
      || hostDragTransient.drag !== S.drag) {
      hostDragTransient = {
        project: S.project, sheetId: S.drawingSheets?.activeSheetId,
        drag: S.drag, values: new Map()
      };
    }
    return hostDragTransient.values;
  }
  function placementConfig(value = {}) {
    return { ...value, systemId: owner, uiKey: toolKey || `${owner.replaceAll('-', '_')}_placement` };
  }
  function assertAssetPath(path) {
    if (ownStaticAssets.has(path)) return path;
    const match = /^\/api\/asset-objects\/([^/?#]+)\/file\/[A-Za-z0-9_-]+(?:\?[^#]*)?$/.exec(String(path || ''));
    if (!match || !knownAssetIds.has(decodeURIComponent(match[1]))) {
      throw new TypeError('Das Asset gehoert nicht zu den geladenen Systemartikeln.');
    }
    return String(path);
  }
  function cleanup(callback) {
    try {
      const result = callback();
      if (result && typeof result.then === 'function') Promise.resolve(result).catch(() => undefined);
    } catch { /* Every owner resource still gets its own cleanup attempt. */ }
  }
  function dispose() {
    if (disposed) return;
    disposed = true;
    for (const remove of disposers) cleanup(remove);
    disposers.clear();
    hostDragTransient = null;
    if (ownDrag()) S.drag = { active: false, type: null };
    if (capabilities.has('drawingObjects')) invalidateDrawingObjectSnapshot();
  }
  const runtime = { ids: Object.freeze({ make: makeId }), dispose,
    context: Object.freeze({ ...contextPorts, normalizeLevels: normalizeProjectLevels }),
    onDispose(callback) {
      if (typeof callback !== 'function') throw new TypeError('Dispose benoetigt eine Funktion.');
      if (disposed) { cleanup(callback); return () => undefined; }
      disposers.add(callback);
      return () => disposers.delete(callback);
    }
  };
  if (separateHistory) runtime.history = historyPort('systems');
  if (orderCommandService) runtime.orderData = Object.freeze({
    read: orderCommandService.read, execute: orderCommandService.execute, transaction: orderCommandService.transaction,
    history: historyPort('orderData')
  });
  if (capabilities.has('document')) runtime.document = Object.freeze({
    read: commandService.read,
    readSheets: () => disposed ? Object.freeze([]) : readOwnerSheets(contract,S.project,S.drawingSheets),
    mode: () => projectStateMode(S.project),
    settings: () => Object.freeze({ gridSize: S.project?.gridSize, snap: S.project?.snap })
  });
  if (capabilities.has('commands')) runtime.commands = Object.freeze({
    execute: commandService.execute, applyFromHost: commandService.applyFromHost,
    beginGesture: commandService.beginGesture, updateGesture: commandService.updateGesture,
    commitGesture: commandService.commitGesture, cancelGesture: commandService.cancelGesture
  });
  if (capabilities.has('selection')) runtime.selection = Object.freeze({
    context: () => disposed ? null : readSystemSelectionContext(),
    legacy: selectionFields.legacy,
    patchLegacy: selectionFields.patchLegacy,
    items: () => SelectionService.items().filter(item => item.owner === owner),
    primaryId: () => primaryField ? S.selection?.[primaryField] || null : SelectionService.items().filter(item => item.owner === owner).at(-1)?.id || null,
    replace: replaceSelection,
    clear() {
      if (disposed) return false;
      const hadSelection = SelectionService.items().some(item => item.owner === owner);
      replaceSelection([]);
      return hadSelection;
    },
    clearOthers: () => !disposed && SelectionService.clearSystemSelectionsExcept(owner),
    hasMultiSelection
  });
  runtime.transient = Object.freeze({
    get: key => ownTransient().get(String(key)),
    set(key, value) { ownTransient().set(String(key), value); return value; },
    clipboardPreviews: () => readonly(cloneSystemData((S.ui?.clipboardPastePending?.previews || []).filter(preview => preview.systemId === owner)))
  });
  runtime.interaction = Object.freeze({
    getDrag: () => disposed ? null : ownDrag(),
    context: () => disposed ? Object.freeze({ active: false, type: null, token: null }) : readSystemInteractionContext(),
    constrainPointToOrthogonal,
    claimPointerCommit: () => !disposed && capabilities.has('commands')
      && claimPointerCommit(S.drag, owner, S.project, S.drawingSheets?.activeSheetId),
    invalidatePreviewSnapshot: () => {
      if (disposed || !capabilities.has('commands')) return false;
      invalidateDrawingObjectSnapshot();
      return true;
    },
    getDragTransient: key => currentDragTransient()?.get(String(key)),
    setDragTransient(key, value) {
      const values = currentDragTransient();
      if (!values) return false;
      values.set(String(key), value);
      return true;
    },
    setDrag(value) {
      if (disposed) return null;
      if (S.drag?.active && S.drag.systemId !== owner) throw new Error('Eine fremde Geste ist noch aktiv.');
      const drag = { ...value, systemId: owner, owner };
      S.drag = drag;
      dragContext = { drag, project: S.project, sheetId: S.drawingSheets?.activeSheetId };
      dragView = new Proxy(drag, {
        set(target, key, next) {
          if (disposed || ['systemId', 'owner'].includes(key) && next !== owner) return false;
          target[key] = next;
          return true;
        },
        deleteProperty(target, key) {
          if (disposed || ['systemId', 'owner'].includes(key)) return false;
          return Reflect.deleteProperty(target, key);
        }
      });
      return dragView;
    },
    clearDrag() {
      if (disposed || !ownDrag()) return false;
      S.drag = { active: false, type: null };
      dragContext = null;
      return true;
    }
  });
  if (capabilities.has('tools')) runtime.tools = Object.freeze({
    active: () => {
      const tool = !disposed && activeTool();
      return tool ? Object.freeze({ owner: tool.owner, tool: tool.tool }) : null;
    },
    canStartPointer: event => !disposed && isPlainPrimaryPointer(event)
      && (!activeTool() || activeTool().owner === owner),
    value: () => !disposed && toolKey ? S.ui?.[toolKey] ?? null : null,
    setValue(value) {
      if (disposed || !toolKey || value !== null && typeof value !== 'string') return false;
      S.ui ||= {};
      S.ui[toolKey] = value;
      return value;
    },
    setPlacementTool: value => !disposed && PlacementTools.setPlacementTool(placementConfig(value)),
    activePlacementToolValue: value => disposed ? '' : PlacementTools.activePlacementToolValue(placementConfig(value)),
    clearPlacementTool: value => !disposed && PlacementTools.clearPlacementTool(placementConfig(value)),
    syncPlacementToolFromLegacy: value => !disposed && PlacementTools.syncPlacementToolFromLegacy(placementConfig(value))
  });
  runtime.render = Object.freeze({
    setStatus: (...args) => { if (!disposed) setStatus(...args); },
    showDrawingFeedback: (...args) => { if (!disposed) showDrawingFeedback(...args); },
    requestPlanRender: (...args) => { if (!disposed) AppRuntime.requestPlanRender(...args); },
    requestAppRefresh: (...args) => { if (!disposed) AppRuntime.requestAppRefresh(...args); },
    isPrintPlanCaptureInProgress,
    shouldRenderPrintPlanCoreGroup: key => String(key).startsWith(`${owner}:`) && shouldRenderPrintPlanCoreGroup(key),
    selectedPrintPlanSystemObjectIds: options => selectedPrintPlanSystemObjectIds(owner, options),
    activeDrawingSheetPositionPrefix: id => AppRuntime.activeDrawingSheetPositionPrefix(owner, id)
  });
  if (capabilities.has('render2D')) runtime.viewport = Object.freeze({ getCanvasSize, getPlanSvg, screenToWorld, setStatus, svgEl, worldSvgRect, worldToScreen,
    camera: () => readonly(cloneSystemData(S.camera2D || {})), resolvePlanLabelMetrics });
  if (capabilities.has('drawingObjects')) runtime.drawings = Object.freeze({
    clearDrawingObjects: () => DrawingRegistry.clearDrawingObjects({ systemType: owner }),
    registerDrawingObject(definition) {
      if (definition.systemType && definition.systemType !== owner) throw new TypeError('Fremder DrawingObject-Owner.');
      return DrawingRegistry.registerDrawingObject({ ...definition, systemType: owner });
    },
    getDrawingObject: drawingObject,
    drawingObjectState(object) {
      if (!ownObject(object)) return null;
      const registered = drawingObject(object.id);
      if (!registered) return null;
      const state = DrawingRegistry.drawingObjectState(registered);
      if (!state) return null;
      try {
        return readonly(cloneSystemData(state));
      } catch {
        return null;
      }
    },
    clearDrawingSelection(options) {
      const previous = DrawingSelection.getSelectedDrawingIds();
      const ownIds = previous.filter(id => drawingObject(id));
      const retainedIds = previous.filter(id => !drawingObject(id));
      if (retainedIds.length) DrawingSelection.setDrawingSelection(retainedIds);
      else DrawingSelection.clearDrawingSelection(options);
      return ownIds;
    },
    setDrawingSelection: ids => objectsForOwner(DrawingSelection.setDrawingSelection([
      ...DrawingSelection.getSelectedDrawingIds().filter(id => !drawingObject(id)),
      ...ids.filter(id => drawingObject(id))
    ])),
    createTransformSnapshot: objects => DrawingTransforms.createTransformSnapshot(objectsForOwner(objects)),
    moveDrawingObjects: (objects, delta, options) => withHostDraft(() => DrawingTransforms.moveDrawingObjects(objectsForOwner(objects), delta, options)),
    applyTransformSnapshotDelta: (snapshot, delta, options) => withHostDraft(() => DrawingTransforms.applyTransformSnapshotDelta(snapshotForOwner(snapshot), delta, options)),
    applyTransformSnapshotRotation: (snapshot, angle, options) => withHostDraft(() => DrawingTransforms.applyTransformSnapshotRotation(snapshotForOwner(snapshot), angle, options)),
    transformSnapshotBounds: snapshot => DrawingTransforms.transformSnapshotBounds(snapshotForOwner(snapshot))
  });
  if (capabilities.has('assets')) runtime.assets = Object.freeze({
    packages: Object.freeze({ resolve: packageReader.resolve, open: packageReader.open, readArtifact: packageReader.readArtifact }),
    // Publish local asset state first, then invalidate derived geometry before
    // requesting a refresh. Persisted model revision and history stay intact.
    invalidateGeometry() {
      if (disposed) return false;
      invalidatePresentation();
      if (!capabilities.has('drawingObjects')) return false;
      invalidateDrawingObjectSnapshot();
      return true;
    },
    async listDrawingAssets() {
      if (disposed) return { items: [] };
      const response = await fetch(`/api/asset-objects?assetKind=drawing&systemCode=${encodeURIComponent(owner)}`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      if (disposed) return { items: [] };
      const items = (Array.isArray(payload.items) ? payload.items : []).filter(item => item && typeof item === 'object' && (!item.systemCode || item.systemCode === owner));
      knownAssetIds.clear(); knownAssetRevisions.clear(); knownAssetRows.clear();
      items.forEach(item => {
        knownAssetIds.add(String(item.id)); knownAssetRevisions.set(String(item.id), item.currentRevisionId || null);
        knownAssetRows.set(String(item.id), Object.freeze({ revisionId: item.currentRevisionId || null, sourceSha256: item.sourceSha256 || null,
          planStrokeWidthFactor: item.planStrokeWidthFactor ?? 1, colorRgbOverride: item.colorRgbOverride ?? null, updatedAt: item.updatedAt || '' }));
      });
      return { ...payload, items };
    },
    onChanged(callback) {
      if (disposed) return () => undefined;
      const handler = () => { if (!disposed) { invalidatePresentation(); callback(); } };
      if (typeof window !== 'undefined') window.addEventListener('ks:asset-objects-changed', handler);
      const remove = () => {
        if (typeof window !== 'undefined') window.removeEventListener('ks:asset-objects-changed', handler);
        disposers.delete(remove);
      };
      disposers.add(remove);
      return remove;
    },
    async readJson(path) {
      if (disposed) return null;
      const response = await fetch(assertAssetPath(path), { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const value = await response.json();
      return disposed ? null : value;
    },
    async loadBoundSvg(reference, appearance = null, viewId = null) {
      if (disposed) return { status: 'disposed' };
      const context = getAssetContext(), appearanceKey = legacyAppearanceKey(appearance);
      const kind = viewId ? 'legacyView' : 'legacySvg';
      const result = await packageReader.readArtifact(reference, kind, viewId || undefined);
      if (disposed || context !== getAssetContext()) return { status: disposed ? 'disposed' : 'cancelled' };
      if (result.status !== 'ready') return result;
      const bytes = applyLegacyAppearance(kind, result.bytes, appearance, viewId);
      return { status: 'ready', asset: parseImportedSvgAsset(new TextDecoder('utf-8', { fatal: true }).decode(bytes),
        `${assetReferenceKey(reference)}${viewId ? `|view:${viewId}` : ''}${appearanceKey ? `|${appearanceKey}` : ''}`) };
    },
    // U08c Block 5: monochrome GPU plan document of an explicit revision.
    loadBoundPlanProjection: (reference, viewId) => loadPlanProjection(reference, viewId),
    async loadCurrentPlanProjection(assetObjectId, viewId) {
      if (disposed) return { status: 'disposed' };
      const id = String(assetObjectId), row = knownAssetRows.get(id);
      if (!row || !Number.isSafeInteger(Number(assetObjectId))) return { status: 'missing-asset' };
      if (!row.revisionId) return { status: 'unversioned-asset' };
      const context = getAssetContext();
      const resolved = await packageReader.resolve({ assetObjectId: Number(id), revisionId: row.revisionId });
      if (disposed || context !== getAssetContext()) return { status: disposed ? 'disposed' : 'cancelled' };
      if (resolved.status !== 'ready') return resolved;
      return loadPlanProjection(resolved.reference, viewId);
    },
    async resolveCurrentPin(assetObjectId, options = {}) {
      if (disposed) return { status: 'disposed' };
      const id = String(assetObjectId), row = knownAssetRows.get(id);
      if (!row || !Number.isSafeInteger(Number(assetObjectId))) return { status: 'missing-asset' };
      if (!row.revisionId) return { status: 'unversioned-asset' };
      const context = getAssetContext(), stale = () => disposed || context !== getAssetContext();
      const resolved = await packageReader.resolve({ assetObjectId: Number(id), revisionId: row.revisionId });
      if (stale()) return { status: disposed ? 'disposed' : 'cancelled' };
      if (resolved.status !== 'ready') return resolved;
      if (row.sourceSha256 && resolved.reference.sourceSha256 !== row.sourceSha256) return { status: 'invalid-package' };
      // The current files are the pristine package outputs recoloured by the asset
      // modal. Derive and verify that recolouring instead of trusting stored hints.
      let regions = {};
      const pristine = await packageReader.readArtifact(resolved.reference, 'legacySvg');
      if (stale()) return { status: disposed ? 'disposed' : 'cancelled' };
      if (pristine.status === 'ready') {
        let served;
        try {
          const response = await fetch(`/api/asset-objects/${id}/file/svg?v=${encodeURIComponent(row.updatedAt)}`, { cache: 'no-store' });
          if (!response.ok) return stale() ? { status: 'cancelled' } : { status: 'read-error' };
          served = await response.text();
        } catch { return stale() ? { status: 'cancelled' } : { status: 'read-error' }; }
        if (stale()) return { status: disposed ? 'disposed' : 'cancelled' };
        try { regions = deriveLegacySvgRegionColors(new TextDecoder('utf-8', { fatal: true }).decode(pristine.bytes), served); }
        catch { return { status: 'appearance-mismatch' }; }
      } else if (pristine.status !== 'missing-artifact') return pristine;
      // Optional: colour state of the individual legacy projection views as served now.
      const viewColors = {};
      if (options?.views === true) for (const view of ['top', 'bottom', 'front', 'back', 'left', 'right']) {
        const pristineView = await packageReader.readArtifact(resolved.reference, 'legacyView', view);
        if (stale()) return { status: disposed ? 'disposed' : 'cancelled' };
        if (pristineView.status === 'missing-artifact') continue;
        if (pristineView.status !== 'ready') return pristineView;
        let servedView;
        try {
          const response = await fetch(`/api/asset-objects/${id}/file/svg?view=${view}&v=${encodeURIComponent(row.updatedAt)}`, { cache: 'no-store' });
          if (!response.ok) return stale() ? { status: 'cancelled' } : { status: 'read-error' };
          servedView = await response.text();
        } catch { return stale() ? { status: 'cancelled' } : { status: 'read-error' }; }
        if (stale()) return { status: disposed ? 'disposed' : 'cancelled' };
        try {
          const colors = deriveLegacySvgRegionColors(new TextDecoder('utf-8', { fatal: true }).decode(pristineView.bytes), servedView);
          if (Object.keys(colors).length) viewColors[view] = colors;
        } catch { return { status: 'appearance-mismatch' }; }
      }
      let appearance;
      try {
        appearance = createLegacyAssetAppearance({ legacyGlbColor: row.colorRgbOverride, legacySvgRegionColors: regions,
          ...(Object.keys(viewColors).length ? { legacySvgViewRegionColors: viewColors } : {}) });
      } catch { return { status: 'appearance-mismatch' }; }
      const capability = resolved.definition?.capability || {};
      const limits = value => Object.freeze({ min: value.min, max: value.max, default: value.default });
      const panelKeys = ['widthMm', 'heightMm', 'thicknessMm'];
      const definitions = capability.parameterDefinitions && typeof capability.parameterDefinitions === 'object' ? capability.parameterDefinitions : null;
      const geometry = capability.kind === 'profile-extrusion' && capability.lengthMm
        ? { kind: 'profile-extrusion', lengthMm: limits(capability.lengthMm) }
        : (capability.kind === 'panel' && panelKeys.every(key => definitions?.[key]))
          || (capability.kind === 'assembly' && definitions && Object.keys(definitions).length)
          ? { kind: capability.kind, parameterDefinitions: Object.freeze(Object.fromEntries(Object.keys(definitions).sort().map(key => [key, limits(definitions[key])]))) }
          : { kind: typeof capability.kind === 'string' ? capability.kind : 'unknown' };
      return Object.freeze({ status: 'ready', reference: resolved.reference, appearance, geometry: Object.freeze(geometry) });
    },
    /** Idempotent backend request of one variant of an owner asset; never changes the document. */
    async requestVariant(base, parameters) {
      if (disposed) return { status: 'disposed' };
      let checked;
      try { checked = checkRequest({ base, parameters }); authorizeAsset(checked.base.assetObjectId, checked.base); }
      catch { return Object.freeze({ status: 'invalid-request' }); }
      const context = getAssetContext(), stale = () => disposed || context !== getAssetContext();
      let response;
      try {
        response = await fetch(`/api/asset-objects/${checked.base.assetObjectId}/revisions/${checked.base.revisionId}/variants`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', cache: 'no-store',
          body: JSON.stringify({ parameters: checked.parameters }) });
      } catch { return stale() ? { status: 'cancelled' } : Object.freeze({ status: 'read-error' }); }
      if (stale()) return { status: disposed ? 'disposed' : 'cancelled' };
      if (!response.ok) return Object.freeze({ status: response.status === 400 ? 'invalid-request' : 'read-error' });
      let payload;
      try { payload = await response.json(); } catch { return Object.freeze({ status: 'read-error' }); }
      if (stale()) return { status: disposed ? 'disposed' : 'cancelled' };
      if (payload?.variantId !== checked.variantId || !['queued', 'running', 'published', 'failed'].includes(payload.status)) return Object.freeze({ status: 'invalid-package' });
      return Object.freeze({ status: payload.status, variantId: checked.variantId, ...(payload.errorCode ? { errorCode: String(payload.errorCode) } : {}) });
    },
    async resolveVariant(value) {
      if (disposed) return { status: 'disposed' };
      try { return await resolveVariant(value); } catch { return Object.freeze({ status: 'invalid-request' }); }
    },
    async loadBoundVariantModel(value, appearance = null, options = {}) {
      if (disposed) return { status: 'disposed' };
      if (appearance) createLegacyAssetAppearance(appearance);
      const context = getAssetContext();
      let resolved;
      try { resolved = await resolveVariant(value); } catch { return { status: 'invalid-request' }; }
      if (disposed || context !== getAssetContext()) return { status: disposed ? 'disposed' : 'cancelled' };
      if (resolved.status !== 'ready') return resolved;
      const reader = await boundReader();
      if (disposed || context !== getAssetContext()) return { status: disposed ? 'disposed' : 'cancelled' };
      return reader.loadVariant(resolved.reference, resolved.base, appearance, options.prepareOnly === true);
    },
    async loadBoundModel(reference, appearance = null, options = {}) {
      if (disposed) return { status: 'disposed' };
      if (appearance) createLegacyAssetAppearance(appearance);
      const context = getAssetContext();
      const reader = await boundReader();
      if (disposed || context !== getAssetContext()) return { status: disposed ? 'disposed' : 'cancelled' };
      return reader.load(reference, appearance, options.prepareOnly === true);
    },
    loadImportedSvgAsset: (path, options) => {
      const safePath = assertAssetPath(path);
      const id = /\/asset-objects\/(\d+)\//.exec(safePath)?.[1];
      return loadImportedSvgAsset(safePath, { ...options, planStrokeWidthFactor: knownAssetRows.get(id)?.planStrokeWidthFactor ?? 1 });
    },
    drawImportedSvgAsset,
    computeDimensionSnapMetadata(options) {
      if (options?.dimensionGeometryPath) assertAssetPath(options.dimensionGeometryPath);
      return computeDimensionSnapMetadata(options);
    },
    async loadModel(path, ...options) {
      const assetPath = assertAssetPath(path);
      await prepare3D();
      return threeModules.loadModel(assetPath, ...options);
    },
    async loadRigidPresentation(path, mode = 'full') {
      if (disposed) return null;
      const assetPath = assertAssetPath(path), generation = assetReadGeneration;
      const match = /^\/api\/asset-objects\/(\d+)\/file\/glb(?:\?|$)/.exec(assetPath);
      const id = match ? Number(match[1]) : null;
      const revision = match ? knownAssetRevisions.get(match[1]) : null;
      if (!presentationReaderPromise) presentationReaderPromise = prepare3D()
        .then(() => disposed ? null : threeModules.createRigidPresentationReader())
        .then(reader => { if (disposed) { reader?.dispose(); return null; } presentationReader = reader; return reader; })
        .catch(error => { presentationReaderPromise = null; throw error; });
      const reader = await presentationReaderPromise;
      if (!reader || disposed || generation !== assetReadGeneration) return null;
      return reader.load({ path: assetPath, id, revision, mode });
    },
    async ensureThreeRuntime() {
      await prepare3D();
      return threeModules.ensureThreeRuntime();
    },
    getModelAssetSourceMetrics: (...args) => threeCall('getModelAssetSourceMetrics', args)
  });
  if (capabilities.has('render3D')) runtime.prepare3D = prepare3D;
  if (capabilities.has('materials')) runtime.materials = Object.freeze({
    parseColorHex,
    prepareAssetTextureUvs: (...args) => threeCall('prepareAssetTextureUvs', args),
    applyMaterialToObjectMeshes: (...args) => threeCall('applyMaterialToObjectMeshes', args),
    createThreeMaterialById: (...args) => threeCall('createThreeMaterialById', args),
    materialIdForColorValue3D: (...args) => threeCall('materialIdForColorValue3D', args),
    materialSurfaceForColorValue3D: (...args) => threeCall('materialSurfaceForColorValue3D', args),
    colorMaterialOptions3D: (...args) => threeCall('colorMaterialOptions3D', args),
    repeatedTextureForColorValue3D: (...args) => threeCall('repeatedTextureForColorValue3D', args)
  });
  if (capabilities.has('colors')) runtime.colors = Object.freeze({
    colorCatalogEntries, colorValueKey, findColor, firstColor,
    allowedCatalogIds: usage => disposed ? Object.freeze([]) : systemColorCatalogIds(owner, usage),
    colorPickerAllowedCatalogsForUsage: usage => disposed ? Object.freeze([]) : systemColorCatalogIds(owner, usage),
    normalizeColorValue, resolveColorHex, resolveColorLabel,
    serializeColorValue, bindColorPicker, colorPickerSwatchStyle, renderColorPicker
  });
  if (capabilities.has('articles')) runtime.articles = createArticleReadPort(ArticleDatabaseCatalog, () => disposed);
  if (capabilities.has('sidebarCards')) {
    const cards = createSystemSidebarCards(owner, {
      current: () => disposed ? null : runtime.context.current(),
      isActive: () => SystemRegistry.state.activeSystemId === owner
    });
    disposers.add(cards.dispose);
    runtime.sidebarCards = Object.freeze({ register: cards.register });
  }
  if (capabilities.has('sidebar')) runtime.sidebar = Object.freeze({
    renderGroupList, selectionPanelHost: () => document.getElementById('selectionPanel')
  });
  if (capabilities.has('sidebar')) runtime.preferences = Object.freeze({
    ownerId: userPreferenceOwnerId,
    get(key, fallback = null) {
      if (disposed || !preferenceKeys.has(key)) return fallback;
      return readonly(cloneSystemData(getUserPreference(key, fallback)));
    },
    set(key, value) {
      if (disposed || !preferenceKeys.has(key)) return Promise.resolve(false);
      return setUserPreference(key, cloneSystemData(value));
    }
  });
  if (capabilities.has('i18n')) runtime.i18n = Object.freeze({
    t(key, fallback) {
      if (!String(key).startsWith(`${owner}.`)) throw new TypeError('Fremder Uebersetzungsnamensraum.');
      const labels = manifest.translations || {};
      return t(key, t(translationAliases[key] || key,
        labels[getActiveLanguage()]?.[key] ?? labels.DE?.[key] ?? fallback));
    }
  });
  return Object.freeze(runtime);
}
